'use client'

import { Star, Bookmark } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { RECIPE_CARD_SIZES } from '@/lib/recipeCardSizes'

interface CardProps {
  className?: string
  src: string
  /** Decode-width hint only; does not change crop. Defaults to RECIPE_CARD_SIZES. */
  sizes?: string
  link: string
  farmerCount?: number | undefined
  title: string
  rating?: number
  numberOfIngredients?: number
  reviewCount?: number
  time?: string | number | null | undefined
  slug?: string
  userId?: number
  user?: any | undefined
  cardType?: 'recipe' | 'ingredient'
  description?: string | null
  /** Recipes that use this ingredient. The line is omitted when this is missing or zero. */
  recipeCount?: number | null
  /** DirectoryIngredient.isSeasonal — the job sets this for ingredients in season this month. */
  inSeason?: boolean
  recipeId?: number
  isBookmarked?: boolean
  onToggleBookmark?: (recipeId: number) => void
}

const DESCRIPTION_LIMIT = 100

function shortDescription(html?: string | null) {
  if (!html) return ''
  const plain = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
  if (plain.length <= DESCRIPTION_LIMIT) return plain
  return `${plain.slice(0, DESCRIPTION_LIMIT).trimEnd()}…`
}

const convertTime = (time: number | string | null | undefined): string => {
  if (typeof time === 'number') {
    const hours = Math.floor(time / 60)
    const minutes = time % 60

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${
        minutes > 0 ? `${minutes} mins` : ''
      }`
    }

    if (minutes == 0) {
      return 'N/A'
    }

    return `${minutes} mins`
  }

  if (typeof time === 'string') {
    return time
  }

  return 'N/A'
}

const RecipieCard = ({
  className,
  src,
  sizes = RECIPE_CARD_SIZES,
  farmerCount,
  title,
  link,
  rating,
  reviewCount,
  numberOfIngredients,
  time,
  user,
  cardType = 'recipe',
  description,
  recipeCount,
  inSeason,
  recipeId,
  isBookmarked,
  onToggleBookmark
}: CardProps) => {
  const router = useRouter()
  // const userProfile = slug ? useGetUser({slug: slug}) : null

  const finalRating = rating ?? 0 // Default to 0 if rating is undefined or null.

  // next/image throws "Failed to construct 'URL': Invalid URL" for anything
  // that isn't an absolute http(s) URL or a local "/..." path -- callers pass
  // whatever image field their query happens to have, which isn't always one.
  const imgSrc = src && (src.startsWith('http') || src.startsWith('/')) ? src : '/assets/icons/Recipe.svg'

  const handleCardClick = () => {
    if (link) {
      router.push(link)
    }
  }
  
  return (
    <div
      onClick={handleCardClick}
      className={`broder-[#E6E6E6] flex w-full h-full flex-col gap-2 overflow-hidden cursor-pointer rounded-lg border bg-white ${className}`}
    >
      <div className="relative w-full h-48 flex-shrink-0 sm:h-52">
        <Image
          src={imgSrc}
          alt="Client Review"
          fill
          sizes={sizes}
          className="object-cover"
          priority={false}
          loading="lazy"
        />
        {cardType === 'recipe' && recipeId != null && onToggleBookmark && (
          <button
            type="button"
            aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            onClick={(e) => {
              e.stopPropagation()
              onToggleBookmark(recipeId)
            }}
            className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm hover:bg-white transition-colors duration-200"
          >
            <Bookmark
              size={18}
              color={isBookmarked ? '#4A90E2' : '#548828'}
              className={isBookmarked ? 'fill-[#4A90E2]' : ''}
            />
          </button>
        )}
      </div>
      <div className="flex flex-1 flex-col justify-between gap-5 p-3 pt-0">
        <div className={`flex flex-col gap-2 ${cardType === 'ingredient' ? 'h-full flex-1' : ''}`}>
          <h1 className="font-semibold line-clamp-1">{title}</h1>
          {cardType === 'ingredient' ? (
            <>
              <p className="min-h-[15px] truncate font-['Inter'] text-[12px] font-normal leading-[15px] text-[#202020]">
                {farmerCount
                  ? `Grown by ${farmerCount} ${farmerCount === 1 ? 'farmer' : 'farmers'}`
                  : ''}
              </p>
              <p className="line-clamp-2 min-h-[30px] font-['Inter'] text-[12px] font-normal leading-[15px] text-[#202020]">
                {shortDescription(description)}
              </p>
              {inSeason ? (
                <span className="mt-auto inline-flex w-fit rounded-full border border-[#85C349] bg-[#F3FAEB] px-2.5 py-1.5 font-['Inter'] text-xs font-semibold leading-[15px] text-[#2E7D0F]">
                  In Season
                </span>
              ) : null}
              {recipeCount ? (
                <p className="font-['Inter'] text-[12px] font-normal leading-[15px] text-[#202020]">
                  Included in {recipeCount} {recipeCount === 1 ? 'recipe' : 'recipes'}
                </p>
              ) : null}
            </>
          ) : (
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <div
                  key={star}
                  className={`flex h-[14px] w-[14px] items-center justify-center rounded-lg ${
                    star <= finalRating
                      ? 'fill-[#F3B514] text-[#F3B514]'
                      : 'text-[#F3B514]'
                  }`}
                >
                  <Star
                    className={`$${
                      star <= finalRating
                        ? 'fill-[#F3B514] text-[#F3B514]'
                        : 'text-[#F3B514]'
                    } size-[18px] md:!size-5`}
                  />
                </div>
              ))}
              {(reviewCount && reviewCount !== 0) && (
                <span className="text-xs text-[#6B6B6B]">({reviewCount})</span>
              )}
            </div>
          )}
          {!!farmerCount && cardType !== 'ingredient' && (
            <p className="text-xs">
              Grown by <span className="font-bold">{farmerCount}</span> farmers
            </p>
          )}

          {!!numberOfIngredients && cardType === 'recipe' && (
            <p className="text-xs">
              <span>{numberOfIngredients} Ingredients</span>
            </p>
          )}
          {!numberOfIngredients && cardType === 'recipe' && (
            <p className="text-xs">
              <span>No Ingredients added.</span>
            </p>
          )}
        </div>

        {cardType === 'recipe' && (
          <p className="text-xs">Cook time: {convertTime(time)}</p>
        )}
        {user && (
          <div className='flex gap-2 items-center'>
            <div className='w-7 h-7 rounded-full overflow-hidden flex-shrink-0'>
              <Image
                src={user?.default_listing?.photo || user?.profile_photo_resource || '/assets/avatar/user-default.png'}
                alt={user?.full_name || 'user'}
                width={28}
                height={28}
                className='w-full h-full object-cover'
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).src = '/assets/avatar/user-default.png' }}
              />
            </div>
            <span className='text-sm'>{user?.full_name}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default RecipieCard
